<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import store from '../../../store';
import SkiDatePicker from '../../../components/ski/skipass/SkiDatePicker';
import numberMixin from '../../../mixins/numberMixin';

export default {
  name: 'SkiPassSeasonPriceSettings',
  props: {
    seasonId: {
      type: [Number, String],
      default: null,
    },
    new: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SkiDatePicker,
  },
  mixins: [
    numberMixin,
  ],
  data() {
    return {
      seasonItem: Object.assign({}, this.season),
      availableNetOrGross: [
        {
          id: 'net',
          name: 'Net',
        },
        {
          id: 'gross',
          name: 'Gross',
        },
      ],
      keyCardPaymentTypes: [
        {
          id: 'mandatory',
          name: 'Mandatory',
        },
        {
          id: 'deposit',
          name: 'Deposit',
        },
      ],
      keyCardPaymentTypeDeposit: 'deposit',
      doRedirectToNewSeason: false,
    };
  },
  computed: {
    ...mapGetters({
      suppliers: 'skipass/getSkiSuppliers',
      availableSalesRules: 'skipass/getSalesTypes',
    }),
    ...mapState({
      season: state => state.skipass.season,
      seasonItemsLoading: state => state.skipass.season.itemsLoading,
      seasonItemsError: state => state.skipass.season.itemsError,
      seasonSaveLoading: state => state.skipass.season.saveLoading,
      seasonSaveError: state => state.skipass.season.saveError,
      suppliersLoading: state => state.skipass.suppliers.itemsLoading,
      suppliersError: state => state.skipass.suppliers.itemsError,
      salesTypesLoading: state => state.skipass.salesTypes.itemsLoading,
      salesTypesError: state => state.skipass.salesTypes.itemsError,
    }),
    errorOccurred() {
      return this.seasonItemsError || this.seasonSaveError || this.salesTypesError;
    },
    selectSupplierLabel() {
      const totalSuppliers = Object.keys(this.suppliers).length;
      return totalSuppliers
        ? '(found '.concat(totalSuppliers.toString()).concat(' suppliers)')
        : '';
    },
  },
  watch: {
    season: {
      deep: true,
      handler() {
        if (this.doRedirectToNewSeason
          && this.seasonItem.id === undefined
          && this.season.id !== undefined) {
          this.doRedirectToNewSeason = false;
          this.$router.push(`/ski/ski-pass/season/${this.season.id}`);
          this.$emit('newSeasonCreated', { seasonId: this.season.id });
        }
        this.seasonItem = Object.assign({}, this.season);
      },
    },
  },
  methods: {
    ...mapActions({
      loadSeason: 'skipass/seasonWithRelationsBySeasonIdLoad',
      loadSuppliers: 'skipass/suppliersLoad',
      loadAvailableSalesTypes: 'skipass/salesTypesLoad',
      setSeason: 'skipass/setSeason',
      createSeason: 'skipass/seasonCreate',
      saveSeason: 'skipass/seasonSave',
    }),
    allowedDates(date) {
      return date > this.seasonItem.arrivalFrom;
    },
    createOrUpdate() {
      if (this.seasonItem.id) {
        this.saveSeason(this.seasonItem);
      } else if (this.createSeason(this.seasonItem)) {
        this.doRedirectToNewSeason = true;
      }
    },
    onChange(payload) {
      if (payload.arrivalFrom && payload.arrivalFrom > this.seasonItem.arrivalTo) {
        this.setSeason({
          arrivalTo: payload.arrivalFrom,
        });
      }
      this.setSeason(payload);
    },
    onChangeDecimal(key, value) {
      const payload = {};
      payload[key] = this.$options.filters.numberToDots(value);
      this.onChange(payload);
    },
    formatDecimal(value) {
      return `${value}`.replace('.', ',');
    },
  },
  mounted() {
    if (this.season.id) {
      this.loadSeason(this.season.id);
    } else {
      this.seasonItem = Object.assign({}, this.season);
    }
    if (!this.suppliers.length) {
      this.loadSuppliers();
    }
    if (!this.availableSalesRules.length) {
      this.loadAvailableSalesTypes();
    }
  },
  beforeRouteEnter(to, from, next) {
    if (Object.keys(to.params).length === 0 || to.params.new !== undefined) {
      store.dispatch('skipass/seasonByIdLoad', undefined)
        .finally(() => {
          next();
        });
    }

    if (to.params.seasonId) {
      store.dispatch('skipass/seasonWithRelationsBySeasonIdLoad', to.params.seasonId)
        .finally(() => {
          next();
        });
    }
  },
};
</script>

<template>
  <v-container class="pa-0 pl-4 skipass-season" grid-list-xl>
    <h2 class="d-inline-block">
      {{ this.seasonItem.id
        ? $t('ski.skiPriceSettings.title')
        : $t('ski.skiPriceSettings.newSeason')
      }}
    </h2>
    <v-btn
      @click="loadSeason(season.id)"
      :loading="seasonItemsLoading"
      icon
    >
      <v-icon>cached</v-icon>
    </v-btn>
    <div class="text-center pa-1">
      <v-progress-linear
        v-if="this.seasonItemsLoading
          || this.seasonSaveLoading
          || this.suppliersLoading
          || this.salesTypesLoading
        "
        :indeterminate="true"
      />
    </div>
    <v-alert
      :value="this.errorOccurred"
      color="error"
      icon="warning"
      outline
    >
      <span v-html="errorOccurred"></span>
    </v-alert>
    <v-flex xs6>
      <v-text-field
        @input="onChange({ name: $event })"
        :label="$t('ski.skiArea.label')"
        :value="seasonItem.name"
      />
    </v-flex>
    <v-layout>
      <v-flex xs3 md3>
        <v-switch
          @change="onChange({ webActive: $event })"
          :label="$t('ski.skiArea.showOnWeb')"
          :input-value="seasonItem.webActive"
        />
      </v-flex>
      <v-flex xs3 md3>
        <v-switch
          @change="onChange({ active: $event })"
          :label="$t('ski.skiPriceSettings.pricesActive')"
          :input-value="seasonItem.active"
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex class="pa-2" xs3 md3>
        <ski-date-picker
          @onChange="onChange({ arrivalFrom: $event })"
          :date.sync="seasonItem.arrivalFrom"
          :title="$t('ski.datePicker.from')"
        />
      </v-flex>
      <v-flex class="pa-2" xs3 md3>
        <ski-date-picker
          @onChange="onChange({ arrivalTo: $event })"
          :disabled="seasonItem.arrivalFrom === undefined"
          :allowed-dates="allowedDates"
          :date.sync="seasonItem.arrivalTo"
          :title="$t('ski.datePicker.to')"
        />
      </v-flex>
    </v-layout>
    <h3 class="mt-2 mb-1">{{ $t('ski.skiPriceSettings.supplier') }}</h3>
    <v-layout>
      <v-flex sm5 xs5>
        <v-autocomplete
          @change="onChange({ twentySupplierId: $event })"
          :value="seasonItem.twentySupplierId"
          :items="this.suppliers"
          :loading="suppliersLoading"
          :disabled="suppliersLoading"
          :label="selectSupplierLabel"
          item-text="name"
          item-value="id"
        />
      </v-flex>
      <v-flex sm2 xs2>
        <v-select
          @change="onChange({ netOrGross: $event })"
          :value="seasonItem.netOrGross"
          :items="availableNetOrGross"
          :label="$t('ski.skiPriceSettings.netOrGross')"
          item-text="name"
          item-value="id"
          hide-details
        />
      </v-flex>
      <v-flex sm2 xs2>
        <v-text-field
          v-if="seasonItem.netOrGross === 'gross'"
          @change="onChange({ commissionRatio: $event })"
          :value="seasonItem.commissionRatio"
          :placeholder="$t('ski.skiPriceSettings.commission')"
          :label="$t('ski.skiPriceSettings.percentageCommission')"
          type="Number"
          min="0"
          hide-details
        />
        <v-text-field
          v-if="seasonItem.netOrGross === 'net'"
          @change="onChange({ contributionRatio: $event })"
          :value="seasonItem.contributionRatio"
          :placeholder="$t('ski.skiPriceSettings.contribution')"
          :label="$t('ski.skiPriceSettings.percentageContribution')"
          type="Number"
          min="0"
          hide-details
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex sm3 xs3>
        <v-select
          @change="onChange({ salesRule: $event })"
          :value="seasonItem.salesRule"
          :items="this.availableSalesRules"
          :loading="salesTypesLoading"
          :label="$t('ski.skiPriceSettings.rule')"
          item-text="name"
          item-value="id"
          hide-details
        />
      </v-flex>
      <v-flex sm2 xs2>
        <v-text-field
          @change="onChangeDecimal('priceAdjustmentValue', $event)"
          :value="formatDecimal(seasonItem.priceAdjustmentValue)"
          label="Price Adjustment Value"
          hint="This value is multiplied with all prices prior to any calculations"
          persistent-hint
          reverse
        />
      </v-flex>
    </v-layout>
    <h3 class="mt-2 mb-1">{{ $t('ski.skiPriceSettings.keyCard') }}</h3>
    <v-layout>
      <v-flex sm3 xs3>
        <v-select
          @change="onChange({ keyCardPaymentType: $event })"
          :value="seasonItem.keyCardPaymentType"
          :items="keyCardPaymentTypes"
          :label="$t('ski.skiPriceSettings.keyCardPaymentType')"
          item-text="name"
          item-value="id"
          hide-details
        />
      </v-flex>
      <v-flex sm2 xs2>
        <v-text-field
          @change="onChange({ keyCard: $event })"
          v-if="seasonItem.keyCardPaymentType !== keyCardPaymentTypeDeposit"
          :disabled="seasonItem.keyCardPaymentType === keyCardPaymentTypeDeposit"
          :value="seasonItem.keyCard"
          :placeholder="$t('ski.skiPriceSettings.keyCardPrice')"
          label="Key card price"
          reverse
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex xs2 md3>
        <v-text-field
          @change="onChange({ bookDaysBeforeArrival: $event })"
          :value="seasonItem.bookDaysBeforeArrival"
          label="Book days before arrival"
          type="Number"
          min="0"
          hide-details
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex sm8>
        <v-textarea
          @change="onChange({ informationText: $event })"
          :value="seasonItem.informationText"
          label="Customer information text"
          box
        />
      </v-flex>
    </v-layout>
    <div class="text-xs-right pr-3">
      <v-btn
        @click="createOrUpdate"
        :disabled="seasonSaveLoading"
        color="primary"
      >
        {{ this.seasonItem.id ? $t('buttons.save') : $t('buttons.create') }}
      </v-btn>
    </div>
  </v-container>
</template>

<style lang="scss">
  .skipass-season {
    .v-messages {
      display: none;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/ski/skipass/SkiPassSeasonPriceSettings.vue